import { apiDelete, apiGet, apiPost } from "./api";

export type Bucket =
  | "need_reply_now"
  | "awaiting_your_action"
  | "waiting_on_client"
  | "auto_handled_fyi";

export type Priority = "P0" | "P1" | "P2" | "P3";

/** Cockpit lane (brief §1). Coarser than the legacy buckets — used by the
 *  Phase 7 cockpit UI. Today, derived from bucket on the backend until
 *  Haiku triage starts writing it directly. */
export type Lane = "NEEDS_YOU" | "WAITING_ON_OTHERS" | "TASKS" | "FYI";

export type TriageState =
  | "NEW"
  | "AUTO_HANDLED"
  | "TRIAGED"
  | "DRAFTED"
  | "ESCALATED"
  | "AWAITING_REPLY"
  | "DONE";

export type TriageSource = "rule" | "haiku" | "sonnet" | "opus" | "human";

export type RoutedTo = "ops" | "accounts" | "commercial" | "fleet";

/** Entities Haiku is asked to extract. All keys present, values nullable.
 *  Cards render only the keys that have a value. */
export interface Entities {
  lrn: string | null;
  truck: string | null;
  client: string | null;
  amount: string | null;
  po: string | null;
  site: string | null;
}

export interface EmailDraft {
  body: string;
  model: string;
  created_at: string | null;
  edited_by_human: boolean;
}

export interface EmailCardData {
  id: string;
  subject: string;
  from_name: string;
  from_email: string;
  /** Always non-empty. Graph display name → humanized email local-part →
   *  domain label → raw email → "Unknown sender". Used for the card headline
   *  chip when no curated client tag exists. */
  sender_display_name: string;
  /** Curated client registry match. Empty string when the sender isn't in the
   *  registry — the UI falls back to sender_display_name in that case. */
  client_name: string;
  priority: Priority;
  summary_line_1: string;
  summary_line_2: string;
  received_at: string | null;
  has_attachments: boolean;
  bucket: Bucket;
  is_overridden: boolean;
  /** Phase 1.2 additions. Populated for every doc by the API serializer
   *  even when the underlying Mongo doc predates the overhaul. */
  lane: Lane;
  triage_state: TriageState;
  entities: Entities;
  case_id: string | null;
  needs_reply: boolean | null;
  /** Phase 7.1: the cockpit's CaseCard surfaces the routing chip and
   *  escalation badge from the list response so we don't fetch the
   *  detail just to know which marker to render. */
  routed_to: RoutedTo | null;
  escalation_signal: boolean;
}

export type BucketedEmails = Record<Bucket, EmailCardData[]>;

export interface EmailsResponse {
  window_days: number;
  buckets: BucketedEmails;
}

export const BUCKET_ORDER: Bucket[] = [
  "need_reply_now",
  "awaiting_your_action",
  "waiting_on_client",
  "auto_handled_fyi",
];

export const BUCKET_LABEL: Record<Bucket, string> = {
  need_reply_now: "Need Reply Now",
  awaiting_your_action: "Awaiting Your Action",
  waiting_on_client: "Waiting on Client",
  auto_handled_fyi: "Auto-Handled / FYI",
};

/** Locked colors from CLAUDE.md → tailwind.config.js bucket palette. */
export const BUCKET_COLOR_VAR: Record<Bucket, string> = {
  need_reply_now: "#b91c1c",
  awaiting_your_action: "var(--op-amber)",
  waiting_on_client: "var(--op-accent)",
  auto_handled_fyi: "var(--op-text-muted)",
};

export function emptyBuckets(): BucketedEmails {
  return {
    need_reply_now: [],
    awaiting_your_action: [],
    waiting_on_client: [],
    auto_handled_fyi: [],
  };
}

export function getEmails(): Promise<EmailsResponse> {
  return apiGet<EmailsResponse>("/api/emails");
}

export interface EmailRecipient {
  name: string;
  email: string;
}

export interface EmailAttachment {
  name: string;
  size: number;
  content_type: string;
  is_inline: boolean;
}

export interface EmailDetail {
  id: string;
  subject: string;
  from_name: string;
  from_email: string;
  sender_display_name: string;
  to: EmailRecipient[];
  cc: EmailRecipient[];
  client_name: string;
  priority: Priority;
  bucket: Bucket;
  summary_line_1: string;
  summary_line_2: string;
  received_at: string | null;
  importance: string;
  has_attachments: boolean;
  attachments: EmailAttachment[];
  body_html: string;
  body_text: string;
  folder_display_name: string;
  is_overridden: boolean;
  override_at: string | null;
  ai_bucket: Bucket | null;
  ai_priority: Priority | null;
  lane: Lane;
  triage_state: TriageState;
  triage_source: TriageSource | null;
  entities: Entities;
  case_id: string | null;
  needs_reply: boolean | null;
  escalation_signal: boolean;
  routed_to: RoutedTo | null;
  /** Phase 6.1: Haiku's raw suggested owner. Always present (empty
   *  string when the user themselves is the owner). ``routed_to`` is
   *  the validated subset — only one of the four queue values. */
  suggested_owner: RoutedTo | "";
  draft: EmailDraft | null;
}

export function getEmailDetail(id: string): Promise<EmailDetail> {
  return apiGet<EmailDetail>(`/api/emails/${id}`);
}

export interface OverridePayload {
  bucket?: Bucket;
  priority?: Priority;
}

export function postOverride(id: string, payload: OverridePayload): Promise<EmailDetail> {
  return apiPost<EmailDetail>(`/api/emails/${id}/override`, payload);
}

export function deleteOverride(id: string): Promise<EmailDetail> {
  return apiDelete<EmailDetail>(`/api/emails/${id}/override`);
}

/** Phase 4.2: tone pills the Refine overlay exposes. Locked here to
 *  match VALID_TONES on the backend (app/ai/refine_prompt.py). */
export type RefineTone =
  | "more_formal"
  | "shorter"
  | "firmer"
  | "warmer"
  | "add_deadline"
  | "fix_grammar";

export const REFINE_TONE_LABELS: Record<RefineTone, string> = {
  more_formal: "More formal",
  shorter: "Shorter",
  firmer: "Firmer",
  warmer: "Warmer",
  add_deadline: "Add deadline",
  fix_grammar: "Fix grammar",
};

export interface RefineResponse {
  before: string;
  after: string;
  changes_summary: string;
  tones_applied: RefineTone[];
  email: EmailDetail;
}

export function postRefine(
  id: string,
  tones: RefineTone[],
): Promise<RefineResponse> {
  return apiPost<RefineResponse>(`/api/emails/${id}/refine`, { tones });
}

/** Phase 5.2: Approve & send. The ``body`` is what the user pressed
 *  Approve on — not the AI draft, not a server-derived transformation.
 *  Backend never auto-sends; this is the only path that hits Graph
 *  send. */
export interface SendPayload {
  body: string;
  body_type?: "Text" | "HTML";
}

export function postSend(id: string, payload: SendPayload): Promise<EmailDetail> {
  return apiPost<EmailDetail>(`/api/emails/${id}/send`, payload);
}

/** Phase 7: extracted task surfaced in the cockpit's bottom strip. */
export interface CockpitTask {
  id: string;
  title: string;
  owner: RoutedTo | "";
  state: "open" | "done";
  due_at: string | null;
  source_message_id: string | null;
  entity_ref: Record<string, string>;
  created_at: string | null;
}

export interface TasksResponse {
  tasks: CockpitTask[];
  total: number;
}

export function getTasks(state: string = "open"): Promise<TasksResponse> {
  const qs = state ? `?state=${encodeURIComponent(state)}` : "";
  return apiGet<TasksResponse>(`/api/tasks${qs}`);
}

/** Phase 6.2: per-site digest entries. */
export interface DigestEntry {
  id: string;
  site: string;
  headline: string;
  why_it_matters: string;
  recommended_action: string;
  severity: "high" | "medium" | "low";
  client: string;
  lrn: string;
  source_message_id: string | null;
  case_id: string | null;
  created_at: string | null;
  model: string;
}

export interface DigestSite {
  site: string;
  entries: DigestEntry[];
  severity_counts: { high: number; medium: number; low: number };
}

export interface DigestResponse {
  window_days: number;
  total: number;
  sites: DigestSite[];
}

export function getDigest(): Promise<DigestResponse> {
  return apiGet<DigestResponse>("/api/digest");
}

/** Phase 8: cockpit context — shipment + invoice for one email. */
export interface CockpitShipment {
  lrn: string;
  origin: string;
  destination: string;
  status: "in_transit" | "out_for_delivery" | "delivered" | "delayed";
  eta_relative: string;
  truck: string;
}

export interface CockpitInvoice {
  po: string;
  matched_items: number;
  total_items: number;
  amount: string;
  mismatch_reason: string;
}

export interface CockpitContext {
  email_id: string;
  shipment: CockpitShipment | null;
  invoice: CockpitInvoice | null;
  /** "mock" while Phase 8 mocks are in use; "real" once the real
   *  Oprentis + LogisBill adapters are wired. The UI uses this to
   *  badge mocked data clearly during pilot. */
  source: "mock" | "real";
}

export function getCockpitContext(emailId: string): Promise<CockpitContext> {
  return apiGet<CockpitContext>(`/api/cockpit/context/${emailId}`);
}

export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function formatAbsoluteTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Compact relative time — "now", "12m", "3h", "2d". Anything past 7d would
 *  be filtered by the backend, so we don't bother with weeks/months. */
export function relativeTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = now.getTime() - then;
  const sec = Math.max(0, Math.floor(diffMs / 1000));
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  return `${day}d`;
}
